import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';

import { membersQueryOptions } from '@/features/team';
import { session } from '@/shared/session/session';

export interface MemberMultiSelectProps {
  id?: string;
  /** User ids, as the backend stores alert recipients. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/** Active organisation members by user id; the signed-in member is marked so they can pick themself. */
export function MemberMultiSelect({
  id,
  value,
  onChange,
  disabled,
  ...aria
}: MemberMultiSelectProps) {
  const { t } = useTranslation('workflows');
  const { me } = session.useSession();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const members = useQuery({ ...membersQueryOptions(), enabled: open || value.length > 0 });
  const active = (members.data ?? []).filter((m) => m.status === 'active');
  const nameOf = (userId: string) => {
    const member = active.find((m) => m.user.id === userId);
    if (!member) return null;
    const name = member.user.name ?? member.user.email;
    return member.user.id === me?.user.id ? t('members.you', { name }) : name;
  };
  const selectedNames = value.map(nameOf).filter((name): name is string => name !== null);
  const needle = q.trim().toLowerCase();
  const shown = active.filter(
    (m) =>
      !needle ||
      (m.user.name ?? '').toLowerCase().includes(needle) ||
      m.user.email.toLowerCase().includes(needle),
  );
  const toggle = (userId: string, checked: boolean) =>
    onChange(checked ? [...value, userId] : value.filter((v) => v !== userId));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
          {...aria}
        >
          <span className="truncate">
            {value.length === 0
              ? t('members.trigger')
              : selectedNames.length === value.length
                ? selectedNames.join(', ')
                : t('members.selected', { count: value.length })}
          </span>
          <ChevronsUpDown className="opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        {members.isError ? (
          <p className="text-muted-foreground p-2 text-sm">{t('members.unavailable')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('members.search')}
              aria-label={t('members.search')}
              className="h-8"
            />
            {shown.length === 0 && members.isSuccess ? (
              <p className="text-muted-foreground p-2 text-sm">{t('members.empty')}</p>
            ) : null}
            <ul className="max-h-60 overflow-y-auto" aria-label={t('stage.alerts')}>
              {shown.map((member) => {
                const checkboxId = `${id ?? 'members'}-${member.user.id}`;
                return (
                  <li key={member.id} className="flex items-center gap-2 rounded px-2 py-1.5">
                    <Checkbox
                      id={checkboxId}
                      checked={value.includes(member.user.id)}
                      onCheckedChange={(checked) => toggle(member.user.id, checked === true)}
                    />
                    <Label htmlFor={checkboxId} className="flex min-w-0 flex-col items-start gap-0">
                      <span className="truncate">{nameOf(member.user.id)}</span>
                      <span className="text-muted-foreground truncate text-xs font-normal">
                        {member.user.email}
                      </span>
                    </Label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
